// Notification sound using Web Audio API
// No external file needed - generates a pleasant notification sound

let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Play a pleasant notification sound
export const playNotificationSound = (type = 'default') => {
  try {
    const ctx = getAudioContext();
    
    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Different sounds for different notification types
    const sounds = {
      default: { freq: [800, 1000], duration: 0.15, volume: 0.3 },
      message: { freq: [600, 800], duration: 0.12, volume: 0.25 },
      appointment: { freq: [700, 900, 1100], duration: 0.1, volume: 0.3 },
      callback: { freq: [500, 700, 500], duration: 0.2, volume: 0.35 },
      issue: { freq: [400, 300], duration: 0.25, volume: 0.4 },
      urgent: { freq: [800, 600, 800, 600], duration: 0.15, volume: 0.4 }
    };

    const sound = sounds[type] || sounds.default;
    const now = ctx.currentTime;
    
    // Set initial volume
    gainNode.gain.setValueAtTime(sound.volume, now);
    
    // Play frequency sequence
    sound.freq.forEach((freq, i) => {
      const startTime = now + (i * sound.duration);
      oscillator.frequency.setValueAtTime(freq, startTime);
    });
    
    // Fade out
    const totalDuration = sound.freq.length * sound.duration;
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + totalDuration);
    
    oscillator.type = 'sine';
    oscillator.start(now);
    oscillator.stop(now + totalDuration + 0.1);

  } catch (error) {
    console.warn('Could not play notification sound:', error);
  }
};

// Play callback reminder sound (more urgent, repeating)
export const playCallbackReminderSound = (reminderType = '5min_before') => {
  try {
    const ctx = getAudioContext();
    
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const playBeep = (delay, freq, duration) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      const startTime = ctx.currentTime + delay;
      
      oscillator.frequency.setValueAtTime(freq, startTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration + 0.05);
    };

    // Different patterns for different reminder types
    if (reminderType === 'now' || reminderType === 'overdue') {
      // Urgent: 3 beeps
      playBeep(0, 880, 0.15);
      playBeep(0.2, 880, 0.15);
      playBeep(0.4, 1100, 0.2);
    } else {
      // 5 min before: 2 gentle beeps
      playBeep(0, 700, 0.12);
      playBeep(0.18, 900, 0.15);
    }

  } catch (error) {
    console.warn('Could not play callback sound:', error);
  }
};

export default { playNotificationSound, playCallbackReminderSound };
